import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import { ORDER_API, MASTER_API } from "../../apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";

// ── Rounds a numeric value UP to at most 2 decimal places for display
const formatQty = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.ceil(num * 100) / 100;
};

// ── Converts an ISO date (yyyy-mm-dd) to display format (dd-mm-yyyy)
const isoToDMMMY = (iso) => (iso && dayjs(iso, "YYYY-MM-DD", true).isValid())
    ? dayjs(iso).format("DD-MM-YYYY")
    : "";

// ── Auto-inserts dashes as the user types digits: d d - m m - y y y y
const maskDMYInput = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits.slice(0, 2);
    if (digits.length >= 3) out += "-" + digits.slice(2, 4);
    if (digits.length >= 5) out += "-" + digits.slice(4, 8);
    return out;
};

const dmyToIso = (dmy) => {
    const d = dayjs(dmy, "DD-MM-YYYY", true);
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

// ── Date filter input: always shows/accepts dd-mm-yyyy (e.g. "24-09-2026"),
// independent of the browser/OS locale. Value/onChange stay in ISO
// (yyyy-mm-dd) so the rest of the app (API calls, comparisons) is untouched.
// A native date input is tucked behind the calendar icon for quick picking.
const DateInputDMMMY = ({ value, onChange, style }) => {
    const [text, setText] = useState(isoToDMMMY(value));

    useEffect(() => {
        setText(isoToDMMMY(value));
    }, [value]);

    return (
        <div style={{ position: "relative", ...style }}>
            <input
                type="text"
                className="form-control"
                style={{ height: "38px", paddingRight: 34, width: "100%" }}
                placeholder="dd-mm-yyyy"
                maxLength={10}
                value={text}
                onChange={(e) => {
                    const masked = maskDMYInput(e.target.value);
                    setText(masked);
                    if (masked.length === 10) {
                        const iso = dmyToIso(masked);
                        if (iso) onChange(iso);
                    } else if (masked.length === 0) {
                        onChange("");
                    }
                }}
                onBlur={() => {
                    if (text.length !== 10 || !dmyToIso(text)) {
                        setText(isoToDMMMY(value));
                    }
                }}
            />
            <input
                type="date"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                title="Pick a date"
                style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 20,
                    height: 20,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    color: "transparent",
                    colorScheme: "light"
                }}
            />
        </div>
    );
};

// ── Returns the logged-in user's ID (never name/email) — same fallback
//    chain used across the app (GoodsRequest.js / GoodsReturn.js).
const getCurrentUserId = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
        try {
            const parsedUser = JSON.parse(userObj);
            const id = parsedUser.userId ?? parsedUser.user_id ?? parsedUser.id;
            if (id !== undefined && id !== null && id !== "") return id;
        } catch (e) {
            console.error("Error parsing user data:", e);
        }
    }
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
    return "";
};

function ReturnReceipt() {
    const location = useLocation();
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState("active");
    const [selectedRows, setSelectedRows] = useState([]);

    // ── Confirm modal (select To Storage Location per item) ────────
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmItems, setConfirmItems] = useState([]);

    // ── Available Stock for each confirm-modal row, keyed by index ──
    const [availableStock, setAvailableStock] = useState({});

    // ── "Available Stock" detail modal (batch breakdown) ────────────
    const [stockModalData, setStockModalData] = useState(null);
    const [stockModalLoading, setStockModalLoading] = useState(false);

    // ── Filter dropdown option lists ──────────────────────────────
    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    // ── Active filter values ──────────────────────────────────────
    const [filters, setFilters] = useState({
        productType: null,
        category: null,
        subCategory: null,
        storageLocation: null,
        fromDate: "",
        toDate: "",
        productCode: null,
    });

    useEffect(() => {
        fetchReturnData(filters.fromDate, filters.toDate);
        setSelectedRows([]);
    }, [filters.fromDate, filters.toDate]);

    useEffect(() => {
        fetchDropdownOptions();
        fetchProductOptions();
        fetchStorageLocations();
    }, []);

    const fetchDropdownOptions = async () => {
        try {
            const [productTypes, categories, subCategories] = await Promise.all([
                axios.get(`${MASTER_API}/configuration/product-types`),
                axios.get(`${MASTER_API}/configuration/category`),
                axios.get(`${MASTER_API}/configuration/sub-category`),
            ]);

            setProductTypeOptions(
                (productTypes.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );

            setCategoryOptions(
                (categories.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );

            setSubCategoryOptions(
                (subCategories.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );
        } catch (err) {
            console.error("Failed to load filter options:", err);
        }
    };

    const fetchProductOptions = async () => {
        try {
            const res = await axios.get(`${ORDER_API}/goods-return/product-options`);
            const rows = res.data.data || [];

            setProductOptions(
                rows.map((r) => ({
                    value: r.productCode,
                    label: `${r.productCode} - ${r.productDescription}`,
                }))
            );
        } catch (err) {
            console.error("Failed to load product options:", err);
        }
    };

    const fetchStorageLocations = async () => {
        try {
            const res = await axios.get(`${ORDER_API}/goods-return/storage-locations`);
            const rows = res.data.data || [];

            setStorageLocationOptions(
                rows.map((r) => ({
                    value: r.storage_code,
                    label: `${r.storage_code} - ${r.storage_location}`,
                }))
            );
        } catch (err) {
            console.error("Failed to load storage locations:", err);
        }
    };

    const fetchReturnData = async (fromDate, toDate) => {
        try {
            const res = await axios.get(`${ORDER_API}/goods-return/list`, {
                params: { fromDate, toDate },
            });
            setData(res.data.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    // ── Fetches stock for a product_code + to-storage_code and stores
    //    the summed qty for that Confirm modal row (by index)
    const fetchAvailableStock = async (index, productCode, storageCode) => {
        if (!productCode || !storageCode) return;

        try {
            const res = await axios.get(`${ORDER_API}/goods-return/stock-by-batch`, {
                params: { productCode, storageCode },
            });

            const rows = res.data.data || [];
            const total = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);

            setAvailableStock((prev) => ({
                ...prev,
                [index]: total,
            }));
        } catch (err) {
            console.error(err);
        }
    };

    // ── Opens the Available Stock detail modal (batch breakdown) for
    //    the given product_code + storage_code
    const openStockModal = async (productCode, description, storageLabel, storageCode) => {
        setStockModalData({
            product_code: productCode,
            description: description,
            storage_label: storageLabel,
            batches: [],
        });
        setStockModalLoading(true);

        try {
            const res = await axios.get(`${ORDER_API}/goods-return/stock-by-batch`, {
                params: { productCode, storageCode },
            });

            setStockModalData((prev) =>
                prev && prev.product_code === productCode
                    ? { ...prev, batches: res.data.data || [] }
                    : prev
            );
        } catch (err) {
            console.error(err);
        } finally {
            setStockModalLoading(false);
        }
    };

    const handleSelect = (item) => {
        const exists = selectedRows.find((x) => x.id === item.id);
        if (exists) {
            setSelectedRows(selectedRows.filter((x) => x.id !== item.id));
        } else {
            setSelectedRows([...selectedRows, item]);
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(filteredData.filter(item => item.status !== 'Returned'));
        } else {
            setSelectedRows([]);
        }
    };

    const filteredData = useMemo(() => {
        return (data || []).filter((item) => {
            // Active tab: not yet confirmed/posted. Posted tab: already confirmed.
            if (activeTab === "active" && item.status === "Returned") return false;
            if (activeTab === "posted" && item.status !== "Returned") return false;

            if (filters.productCode && item.product_code !== filters.productCode.value)
                return false;

            if (filters.productType && item.product_type_name !== filters.productType.value)
                return false;

            if (filters.category && item.category_name !== filters.category.value)
                return false;

            if (filters.subCategory && item.sub_category_name !== filters.subCategory.value)
                return false;

            if (filters.storageLocation && item.to_storage_code !== filters.storageLocation.value)
                return false;

            return true;
        });
    }, [data, filters, activeTab]);

    const isAllSelected =
        filteredData.length > 0 &&
        filteredData.filter(item => item.status !== 'Returned').every((item) =>
            selectedRows.some((x) => x.id === item.id)
        );

    const openConfirmModal = () => {
        if (selectedRows.length === 0) {
            toast.warning("Please select at least one row to confirm");
            return;
        }

        const temp = selectedRows.map((item) => ({
            ...item,
            to_storage_code: "",
            to_storage_location: "",
        }));

        setConfirmItems(temp);
        setAvailableStock({});
        setShowConfirmModal(true);
    };

    const handleConfirmReceipt = async () => {
        const missing = confirmItems.some((item) => !item.to_storage_code);
        if (missing) {
            toast.warning("Please select a To Storage Location for all items");
            return;
        }

        try {
            const confirmedBy = getCurrentUserId();
            const itemsWithUser = confirmItems.map((item) => ({ ...item, confirmed_by: confirmedBy }));
            await axios.post(`${ORDER_API}/goods-return/confirm`, {
                items: itemsWithUser,
            });

            toast.success("Return Receipt Confirmed Successfully");
            setShowConfirmModal(false);
            setSelectedRows([]);
            fetchReturnData(filters.fromDate, filters.toDate);
        } catch (err) {
            console.error(err);
            toast.error("Failed to confirm Return Receipt");
        }
    };

    useEffect(() => {
        setSelectedRows([]);
    }, [activeTab]);

    const styles = {
        filterCard: {
            background: "#fff",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            marginBottom: "15px",
        },
        compactLabel: {
            fontSize: "12px",
            fontWeight: "600",
            color: "#334155",
            marginBottom: "4px",
            display: "block",
        },
        compactInput: {
            fontSize: "13px",
            height: "32px",
            padding: "4px 8px",
        },
        pageTitleBar: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 15,
        },
        pageTitle: {
            fontSize: "22px",
            fontWeight: "700",
            color: "#1e293b",
        },
        pageSubtitle: {
            fontSize: "13px",
            color: "#64748b",
        },
        tableContainer: {
            background: "#fff",
            borderRadius: 8,
            padding: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            overflowX: "auto",
        },
        erpTable: {
            width: "100%",
            minWidth: "1400px",
            borderCollapse: "collapse",
        },
        erpTh: {
            background: "#f8fafc",
            fontSize: 12,
            fontWeight: 600,
            padding: 10,
            borderBottom: "1px solid #e5e7eb",
            color: "#1e293b",
            whiteSpace: "nowrap",
        },
        erpTd: {
            fontSize: 13,
            padding: 10,
            borderBottom: "1px solid #f1f5f9",
            color: "#334155",
        },
        modalBackdrop: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
        },
        modal: {
            background: "#fff",
            borderRadius: "8px",
            maxHeight: "85vh",
            width: "95%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        },
        modalHeader: {
            padding: "12px 20px",
            borderBottom: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
        },
        modalBody: {
            padding: "20px",
            overflow: "auto",
            flex: 1,
        },
        modalFooter: {
            padding: "12px 20px",
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
        },
        modalTd: {
            fontSize: 13,
            padding: 10,
            borderBottom: "1px solid #f1f5f9",
            color: "#334155",
            whiteSpace: "nowrap",
            verticalAlign: "middle",
        },
        iconBtn: {
            background: "transparent",
            border: "none",
            fontSize: "16px",
            cursor: "pointer",
        },
        cellLink: {
            color: "#2563eb",
            textDecoration: "none",
            fontWeight: 600,
            cursor: "pointer",
        },
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "38px",
            height: "38px",
            fontSize: "13px",
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "38px",
            padding: "0 8px",
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "38px",
        }),
        option: (provided) => ({
            ...provided,
            fontSize: "12px",
            padding: "6px 12px",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "12px",
        }),
    };

    const modalSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "36px",
            fontSize: "13px",
            minWidth: "200px",
            borderRadius: "6px",
            borderColor: "#ced4da",
            boxShadow: "none",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: "0 8px",
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            padding: "0 4px",
        }),
        option: (provided) => ({
            ...provided,
            fontSize: "12px",
            padding: "6px 12px",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "12px",
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 1200,
        }),
    };

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <h2 style={styles.pageTitle}>Return Receipt</h2>
                    <p style={styles.pageSubtitle}>Manage Goods Return Receipts</p>
                </div>
            </div>

            {/* ── Filter Card (Perfect Grid Alignment) ── */}
            <div style={styles.filterCard}>
                {/* Row 1 – Product Type */}
                <div className="row g-3">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productTypeOptions}
                            value={filters.productType}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productType: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                {/* Row 2 – Category / Sub Category / To Storage Location */}
                <div className="row g-3 mt-1">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={categoryOptions}
                            value={filters.category}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, category: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Sub Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={subCategoryOptions}
                            value={filters.subCategory}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, subCategory: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col-md-3">
                        <label style={styles.compactLabel}>To Storage Location</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={storageLocationOptions}
                            value={filters.storageLocation}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, storageLocation: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                {/* Row 3 – Product Code / From Date / To Date (Aligned perfectly with Row 2) */}
                <div className="row g-3 mt-1">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Product Code</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Search by code or description..."
                            options={productOptions}
                            value={filters.productCode}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productCode: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col-md-3">
                        <label style={styles.compactLabel}>From Date</label>
                        <DateInputDMMMY
                            value={filters.fromDate}
                            onChange={(v) => setFilters((prev) => ({ ...prev, fromDate: v }))}
                            style={styles.compactInput}
                        />
                    </div>

                    <div className="col-md-3">
                        <label style={styles.compactLabel}>To Date</label>
                        <DateInputDMMMY
                            value={filters.toDate}
                            onChange={(v) => setFilters((prev) => ({ ...prev, toDate: v }))}
                            style={styles.compactInput}
                        />
                    </div>
                </div>
            </div>
            {/* ── Action Toggles ── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                }}
            >
                <div className="gr-tab-toggle">
                    <button className={activeTab === "active" ? "active-tab" : ""} onClick={() => setActiveTab("active")}>Active</button>
                    <button className={activeTab === "posted" ? "active-tab" : ""} onClick={() => setActiveTab("posted")}>Posted</button>
                </div>
                {activeTab === "active" && (
                    <button className="btn btn-dark" onClick={openConfirmModal}>
                        Confirm
                    </button>
                )}
            </div>

            {/* ── Data Table ───────────────────────────────────── */}
            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            {activeTab === "active" && (
                                <th style={styles.erpTh}>
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </th>
                            )}
                            <th style={styles.erpTh}>Return ID</th>
                            <th style={styles.erpTh}>Batch Number</th>
                            <th style={styles.erpTh}>Product Code</th>
                            <th style={styles.erpTh}>Description</th>
                            <th style={styles.erpTh}>UOM</th>
                            <th style={styles.erpTh}>Qty</th>
                            <th style={styles.erpTh}>Returned Qty</th>
                            <th style={styles.erpTh}>Returned Date</th>
                            <th style={styles.erpTh}>From Storage Location</th>
                            {activeTab === "posted" && (
                                <th style={styles.erpTh}>To Storage Location</th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.map((item, index) => (
                                <tr key={item.id || index}>
                                    {activeTab === "active" && (
                                        <td style={styles.erpTd}>
                                            <input
                                                type="checkbox"
                                                disabled={item.status === 'Returned'}
                                                checked={selectedRows.some((x) => x.id === item.id)}
                                                onChange={() => handleSelect(item)}
                                            />
                                        </td>
                                    )}
                                    <td style={styles.erpTd}>{item.return_id}</td>
                                    <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                                    <td style={styles.erpTd}>
                                        <Link
                                            to={`/cable/product/product-code?view=${encodeURIComponent(
                                                item.product_code
                                            )}&returnTo=${encodeURIComponent(
                                                location.pathname + location.search
                                            )}`}
                                            style={styles.cellLink}
                                        >
                                            {item.product_code}
                                        </Link>
                                    </td>
                                    <td style={styles.erpTd}>{item.productDescription || "-"}</td>
                                    <td style={styles.erpTd}>{item.uom || "-"}</td>
                                    <td style={styles.erpTd}>{formatQty(item.return_qty)}</td>
                                    <td style={styles.erpTd}>{formatQty(item.return_qty)}</td>
                                    <td style={styles.erpTd}>{isoToDMMMY(item.return_date) || item.return_date || "-"}</td>
                                    <td style={styles.erpTd}>{item.from_storage_location || item.from_storage_code || "-"}</td>
                                    {activeTab === "posted" && (
                                        <td style={styles.erpTd}>{item.to_storage_location || item.to_storage_code || "-"}</td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="text-center">
                                    No Records Found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Confirm Return Receipt Modal ───────────────────── */}
            {showConfirmModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px" }}>
                        <div style={styles.modalHeader}>
                            <h3
                                style={{
                                    margin: 0,
                                    color: "#1e293b",
                                    fontSize: "18px",
                                    fontWeight: 600,
                                }}
                            >
                                Confirm Return Receipt
                            </h3>
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                style={styles.iconBtn}
                            >
                                ✖
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            <div className="card p-3 mb-3 bg-light border-0" style={{ overflowX: "auto" }}>
                                <table className="table" style={{ ...styles.erpTable, minWidth: "1100px" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Return ID</th>
                                            <th style={styles.erpTh}>Batch Number</th>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Description</th>
                                            <th style={styles.erpTh}>UOM</th>
                                            <th style={styles.erpTh}>Returned Qty</th>
                                            <th style={styles.erpTh}>Returned Date</th>
                                            <th style={styles.erpTh}>From Storage Location</th>
                                            <th style={styles.erpTh}>Available Stock</th>
                                            <th style={styles.erpTh}>To Storage Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {confirmItems.map((item, index) => (
                                            <tr key={item.id || index}>
                                                <td style={styles.modalTd}>{item.return_id}</td>
                                                <td style={styles.modalTd}>{item.batch_number || "-"}</td>
                                                <td style={styles.modalTd}>
                                                    <Link
                                                        to={`/cable/product/product-code?view=${encodeURIComponent(
                                                            item.product_code
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.product_code}
                                                    </Link>
                                                </td>
                                                <td style={styles.modalTd}>{item.productDescription || "-"}</td>
                                                <td style={styles.modalTd}>{item.uom || "-"}</td>
                                                <td style={styles.modalTd}>{formatQty(item.return_qty)}</td>
                                                <td style={styles.modalTd}>{isoToDMMMY(item.return_date) || item.return_date || "-"}</td>
                                                <td style={styles.modalTd}>
                                                    {item.from_storage_location || item.from_storage_code || "-"}
                                                </td>
                                                <td style={styles.modalTd}>
                                                    {item.to_storage_code ? (
                                                        <span
                                                            style={styles.cellLink}
                                                            onClick={() =>
                                                                openStockModal(
                                                                    item.product_code,
                                                                    item.productDescription,
                                                                    `${item.to_storage_code} - ${item.to_storage_location}`,
                                                                    item.to_storage_code
                                                                )
                                                            }
                                                        >
                                                            {availableStock[index] !== undefined
                                                                ? formatQty(availableStock[index])
                                                                : "..."}
                                                        </span>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                                <td style={{ ...styles.modalTd, minWidth: "220px" }}>
                                                    <Select
                                                        styles={modalSelectStyles}
                                                        placeholder="Select..."
                                                        options={storageLocationOptions}
                                                        value={
                                                            item.to_storage_code
                                                                ? {
                                                                    value: item.to_storage_code,
                                                                    label: `${item.to_storage_code} - ${item.to_storage_location}`,
                                                                }
                                                                : null
                                                        }
                                                        onChange={(val) => {
                                                            const temp = [...confirmItems];
                                                            temp[index].to_storage_code = val ? val.value : "";
                                                            temp[index].to_storage_location = val
                                                                ? val.label.replace(`${val.value} - `, "")
                                                                : "";
                                                            setConfirmItems(temp);

                                                            if (val) {
                                                                fetchAvailableStock(
                                                                    index,
                                                                    item.product_code,
                                                                    val.value
                                                                );
                                                            } else {
                                                                setAvailableStock((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[index];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-dark" onClick={handleConfirmReceipt}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Available Stock Modal (batch breakdown) ─────────── */}
            {stockModalData && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1100 }}
                    onClick={() => setStockModalData(null)}
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "480px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <div>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                    }}
                                >
                                    {stockModalData.product_code} - {stockModalData.description}
                                </p>
                                {stockModalData.storage_label && (
                                    <p
                                        style={{
                                            margin: "2px 0 0",
                                            fontSize: "12px",
                                            color: "#64748b",
                                        }}
                                    >
                                        {stockModalData.storage_label}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setStockModalData(null)}
                                style={styles.iconBtn}
                            >
                                ✖
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            {stockModalLoading ? (
                                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                                    Loading...
                                </p>
                            ) : stockModalData.batches.filter((b) => Number(b.qty) > 0).length > 0 ? (
                                <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Batch No</th>
                                            <th style={styles.erpTh}>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockModalData.batches
                                            .filter((b) => Number(b.qty) > 0)
                                            .map((b, i) => (
                                                <tr key={i}>
                                                    <td style={styles.erpTd}>{b.batch_number || "-"}</td>
                                                    <td style={styles.erpTd}>{formatQty(b.qty)}</td>
                                                </tr>
                                            ))}
                                        <tr>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>Total</td>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>
                                                {formatQty(
                                                    stockModalData.batches.reduce(
                                                        (sum, b) => sum + Number(b.qty || 0),
                                                        0
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                                    No stock available.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
            <style>{`
                .gr-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
                .gr-tab-toggle button { border: none; background: transparent; padding: 6px 18px; font-size: 13px; font-weight: 600; border-radius: 6px; color: #64748b; cursor: pointer; }
                .gr-tab-toggle button.active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,.15); }
            `}</style>
        </Layout>
    );
}

export default ReturnReceipt;